/**
 * KeyValuePairsEditor
 * Configuration for key-value-pairs control
 * Uses LIST/ARRAY PATTERN for pair management
 */

import React, { useCallback } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../../registry/PropertyEditor.types';

interface KeyValuePair {
  key: string;
  value: string;
}

interface KeyValuePairsConfig {
  pairs?: KeyValuePair[];
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  allowDuplicateKeys?: boolean;
  maxPairs?: number;
}

function renderKeyValuePairsEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;
  const config = (control.config || {}) as KeyValuePairsConfig;

  const pairs = config.pairs || [];
  const keyPlaceholder = config.keyPlaceholder || 'key';
  const valuePlaceholder = config.valuePlaceholder || 'value';
  const allowDuplicateKeys = config.allowDuplicateKeys ?? false;
  const maxPairs = config.maxPairs || 0;

  const handleAddPair = useCallback(() => {
    const newPairs = [...pairs, { key: '', value: '' }];
    onConfigChange('pairs', newPairs);
  }, [pairs, onConfigChange]);

  const handleRemovePair = useCallback(
    (index: number) => {
      onConfigChange('pairs', pairs.filter((_, i) => i !== index));
    },
    [pairs, onConfigChange],
  );

  const handleUpdatePair = useCallback(
    (index: number, field: 'key' | 'value', val: string) => {
      const updated = pairs.map((p, i) =>
        i === index ? { ...p, [field]: val } : p,
      );
      onConfigChange('pairs', updated);
    },
    [pairs, onConfigChange],
  );

  const canAddMore = maxPairs === 0 || pairs.length < maxPairs;

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">Key-Value Pairs Configuration</h4>

      <div className="af-properties-tab__group">
        <label htmlFor="key-placeholder-input" className="af-properties-tab__label">
          Key Placeholder
        </label>
        <input
          id="key-placeholder-input"
          type="text"
          value={keyPlaceholder}
          onChange={(e) => onConfigChange('keyPlaceholder', e.target.value)}
          className="af-properties-tab__input"
          aria-label="Key placeholder"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="value-placeholder-input" className="af-properties-tab__label">
          Value Placeholder
        </label>
        <input
          id="value-placeholder-input"
          type="text"
          value={valuePlaceholder}
          onChange={(e) => onConfigChange('valuePlaceholder', e.target.value)}
          className="af-properties-tab__input"
          aria-label="Value placeholder"
        />
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={allowDuplicateKeys}
            onChange={(e) => onConfigChange('allowDuplicateKeys', e.target.checked)}
          />
          Allow Duplicate Keys
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="max-pairs-input" className="af-properties-tab__label">
          Maximum Pairs (0 = unlimited)
        </label>
        <input
          id="max-pairs-input"
          type="number"
          value={maxPairs}
          onChange={(e) => onConfigChange('maxPairs', Number(e.target.value))}
          min="0"
          className="af-properties-tab__input"
          aria-label="Maximum pairs"
        />
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label" style={{ marginBottom: 6 }}>
          Default Pairs
        </label>
        {pairs.length === 0 && <p className="af-properties-tab__info">No pairs configured.</p>}
        {pairs.map((pair, i) => (
          <div key={i} className="af-properties-tab__pair-row">
            <input
              type="text"
              value={pair.key}
              onChange={(e) => handleUpdatePair(i, 'key', e.target.value)}
              className="af-properties-tab__input af-properties-tab__pair-key"
              placeholder={keyPlaceholder}
              aria-label={`Pair ${i + 1} key`}
            />
            <input
              type="text"
              value={pair.value}
              onChange={(e) => handleUpdatePair(i, 'value', e.target.value)}
              className="af-properties-tab__input af-properties-tab__pair-value"
              placeholder={valuePlaceholder}
              aria-label={`Pair ${i + 1} value`}
            />
            <button
              onClick={() => handleRemovePair(i)}
              className="af-properties-tab__button af-properties-tab__button--danger"
              type="button"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          onClick={handleAddPair}
          disabled={!canAddMore}
          className="af-properties-tab__button af-properties-tab__button--primary"
          type="button"
        >
          Add Pair
        </button>
      </div>
    </div>
  );
}

export const KeyValuePairsEditor: PropertyEditor = {
  type: 'key-value-pairs',
  label: 'Key-Value Pairs',
  description: 'Configure key-value pair list with validation',
  render: renderKeyValuePairsEditor,
};
